import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { MinicartHeader } from './MinicartBlocks/MinicartHeader';
import { MinicartUSP } from './MinicartBlocks/MinicartUSP';
import { MinicartItems } from './MinicartBlocks/MinicartItems';
import { MinicartDiscounts } from './MinicartBlocks/MinicartDiscounts';
import { MinicartTotals } from './MinicartBlocks/MinicartTotals';
import { MinicartNote } from './MinicartBlocks/MinicartNote';
import { MinicartActions } from './MinicartBlocks/MinicartActions';
import { MinicartEmptyState } from './MinicartBlocks/MinicartEmptyState';
import { mobileBreakpoint } from '../../../../entrypoints/theme';
import { ObviyoProductRecommendations } from '@src/stories/blocks/ObviyoRecommendations/ObviyoProductRecommendations';
import CarouselArrows from '@src/stories/elements/CarouselArrows/CarouselArrows';

import CartUndoWishlistPopup from '@src/stories/ecommerce/Cart/CartItems/CartUndoWishlistPopup';

import useShoppingBasketCart from '@src/hooks/useShoppingBasketCart';
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import useCurrentTextStyle from '@src/hooks/useCurrentTextStyle';
import useCartItemRemoval from '@src/hooks/useCartItemRemoval';

import { ICartItem } from '@src/types/cart';

interface MinicartProps {
  settings: {
    section?: {
      minicart_style?: string;
      enable_backdrop?: boolean;
      close_on_backdrop_click?: boolean;
      border_radius?: number;
      box_shadow?: string;
      block_gap?: number;
      padding_top_desktop?: number;
      padding_bottom_desktop?: number;
      padding_left_right_desktop?: number;
      padding_top_mobile?: number;
      padding_bottom_mobile?: number;
      padding_left_right_mobile?: number;
    };
    color_schemas?: {
      color_schema?: string;
    };
    blocks?: any[];
  };
}

export const Minicart = ({ settings }: MinicartProps) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const { cartData, updateItemAndFetch, isUpdatingItem, isGlobalCartLoading } = useShoppingBasketCart();
  const { tempRemovedItems, handleRemoveItem, handleUndoRemove, handleUndoWishlist } = useCartItemRemoval();
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [isVisible, setIsVisible] = useState(false);
  // Removed unused basketApiLoading state
  const [hasRecommendations, setHasRecommendations] = useState(false);
  const [scrollableHeight, setScrollableHeight] = useState<number | null>(null);
  const splideRef = useRef<any>(null);

  const overlayRef = useRef<HTMLDivElement>(null);
  const minicartRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(isVisible);

  // Section settings
  const {
    enable_backdrop = true,
    close_on_backdrop_click = true,
    border_radius = 0,
    box_shadow = 'none',
    block_gap = 0,
    padding_top_desktop = 0,
    padding_bottom_desktop = 0,
    padding_left_right_desktop = 0,
    padding_top_mobile = 0,
    padding_bottom_mobile = 0,
    padding_left_right_mobile = 0
  } = settings?.section || {};

  const miniCartClasses = `shadow-${box_shadow}`;
  const miniCartStyles = {
    borderRadius: `${border_radius}px`,
    paddingTop: isMobile ? `${padding_top_mobile}px` : `${padding_top_desktop}px`,
    paddingBottom: isMobile ? `${padding_bottom_mobile}px` : `${padding_bottom_desktop}px`,
    paddingLeft: isMobile ? `${padding_left_right_mobile}px` : `${padding_left_right_desktop}px`,
    paddingRight: isMobile ? `${padding_left_right_mobile}px` : `${padding_left_right_desktop}px`,
    gap: `${block_gap}px`
  };

  // Generate block objects for easier access to settings
  const blocks: Record<string, any> = {};
  settings.blocks?.forEach((block: any) => {
    blocks[block.type] = {
      settings: block.settings,
      id: block.id,
      additional: { ...block }
    };
  });

  const handleQuantityChange = async (item: ICartItem, newQuantity: number) => {
    if (isUpdatingItem) return;

    if (newQuantity >= 1) {
      await updateItemAndFetch(
        {
          key: item.key,
          quantity: newQuantity
        },
        true
      );
    } else {
      await handleRemoveItem(item);
    }
  };

  const handleCartOpen = () => {
    if (!isVisibleRef.current) {
      setIsVisible(true);
      handleHeaderState('visible');
    }
  };

  const handleCartClose = () => {
    setIsVisible(false);
    handleHeaderState('hidden');
  };

  const handleCartToggle = () => {
    if (isVisibleRef.current) {
      handleCartClose();
    } else {
      handleCartOpen();
    }
  };

  const handleHeaderState = (state: string) => {
    window.dispatchEvent(
      new CustomEvent('block-builder-state-change', {
        detail: {
          state: state,
          reparentSelector: '.header-block-builder',
          componentId: 'minicart'
        }
      })
    );
  };

  // Handle block builder state change to close minicart if it becomes visible
  const handleBlockBuilderStateChange = (event: Event) => {
    const customEvent = event as CustomEvent;
    if (customEvent.detail.state === 'visible' && isVisibleRef.current) {
      handleCartClose();
    }
  };

  const tempRemovedItemsStyle = {
    paddingLeft: isMobile
      ? blocks?.items?.settings?.padding_left_right || blocks?.items?.settings?.item_padding_left_mobile
      : blocks?.items?.settings?.padding_left_right || blocks?.items?.settings?.item_padding_left,
    paddingRight: isMobile
      ? blocks?.items?.settings?.padding_left_right || blocks?.items?.settings?.item_padding_right_mobile
      : blocks?.items?.settings?.padding_left_right || blocks?.items?.settings?.item_padding_right
  };

  useEffect(() => {
    window.addEventListener('cart:open', handleCartOpen);
    window.addEventListener('cart:close', handleCartClose);
    window.addEventListener('cart:toggle', handleCartToggle);
    window.addEventListener('block-builder-state-change', handleBlockBuilderStateChange);

    return () => {
      window.removeEventListener('cart:open', handleCartOpen);
      window.removeEventListener('cart:close', handleCartClose);
      window.removeEventListener('cart:toggle', handleCartToggle);
      window.removeEventListener('block-builder-state-change', handleBlockBuilderStateChange);
    };
  }, [isVisible]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  // Prevent horizontal scrollbar during minicart animation
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflowX = 'hidden';
    } else {
      document.body.style.overflowX = '';
    }

    return () => {
      document.body.style.overflowX = '';
    };
  }, [isVisible]);

  // Measure scrollable height when minicart becomes visible
  useEffect(() => {
    if (isVisible && scrollableRef.current) {
      const measureHeight = () => {
        if (scrollableRef.current) {
          const height = scrollableRef.current.getBoundingClientRect().height;
          setScrollableHeight(height);
        }
      };

      // Measure immediately and after a small delay to ensure layout is complete
      measureHeight();
      const timeoutId = setTimeout(measureHeight, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isVisible, cartData]);

  return createPortal(
    <>
      {enable_backdrop && close_on_backdrop_click && (
        <CSSTransition
          in={isVisible}
          nodeRef={overlayRef}
          timeout={300}
          classNames='fade'
          unmountOnExit
        >
          <div
            ref={overlayRef}
            className='minicart-overlay absolute top-0 md:top-full w-full h-dvh md:h-[calc(100dvh-var(--header-height))] bg-black/50 z-50 transition-all duration-300 ease-out'
            onClick={handleCartClose}
          />
        </CSSTransition>
      )}

      <CSSTransition
        in={isVisible}
        nodeRef={minicartRef}
        timeout={300}
        classNames='minicart'
        unmountOnExit
      >
        <div
          ref={minicartRef}
          className={`minicart absolute max-md:w-full max-w-full z-50 top-0 md:top-full right-0 md:px-6 md:py-4 h-dvh md:h-[calc(100dvh-var(--header-height))] overflow-hidden`}
        >
          <div
            className={`flex flex-col w-full md:w-[440px] h-full overflow-hidden max-md:!rounded-none color-${settings?.color_schemas?.color_schema} ${miniCartClasses}`}
            style={miniCartStyles}
          >
            {/* Header */}
            {blocks.header && (
              <MinicartHeader
                settings={blocks.header.settings}
                block={blocks.header.additional}
                item_count={cartData?.item_count || 0}
                onClose={handleCartClose}
              />
            )}

            {blocks.usp && (
              <MinicartUSP
                settings={blocks.usp.settings}
                block={blocks.usp.additional}
              />
            )}

            {blocks.free_shipping &&
              cartData?.items &&
              cartData.items.length > 0 &&
              cartData.items_subtotal_price &&
              cartData.items_subtotal_price > 0 && (
                <MinicartDiscounts
                  cart_subtotal={cartData.items_subtotal_price / 100}
                  settings={blocks.free_shipping.settings}
                  block={blocks.free_shipping.additional}
                />
              )}

            {/* Undo notifications for temporarily removed items using CartUndoWishlistPopup */}
            <div
              className={`removed-items-notifications ${
                cartData && cartData.items && cartData.items.length === 0 && tempRemovedItems.length > 0
                  ? 'pt-4'
                  : cartData && cartData.items && cartData.items.length > 0 && tempRemovedItems.length > 0
                  ? 'pb-3'
                  : ''
              }`}
              style={tempRemovedItemsStyle}
            >
              <TransitionGroup className='space-y-2'>
                {tempRemovedItems.map((item) => {
                  const popupRef = React.createRef<HTMLDivElement>();
                  return (
                    <CSSTransition
                      key={item.removedAt}
                      nodeRef={popupRef}
                      timeout={300}
                      classNames='minicart'
                    >
                      <div ref={popupRef}>
                        <CartUndoWishlistPopup
                          item={item}
                          settings={blocks?.items?.settings}
                          onClickUndo={() => handleUndoRemove(item)}
                          onClickWishlist={() => handleUndoWishlist(item)}
                          initialWishlistState={item.initialWishlistState}
                        />
                      </div>
                    </CSSTransition>
                  );
                })}
              </TransitionGroup>
            </div>

            <div
              className={`flex-1 overflow-x-hidden custom-scrollbar relative grow min-h-0 ${
                isGlobalCartLoading ? 'overflow-y-hidden pointer-events-none' : 'overflow-y-auto'
              }`}
              ref={scrollableRef}
              x-data='customScrollbar'
            >
              <div className={`relative ${cartData && cartData.items && cartData.items.length > 0 ? '' : 'h-full'}`}>
                <div className='h-full'>
                  {blocks.items && cartData?.items && cartData.items.length > 0 ? (
                    <MinicartItems
                      settings={blocks.items.settings}
                      block={blocks.items.additional}
                      items={cartData.items}
                      onQuantityChange={handleQuantityChange}
                      onRemoveItem={handleRemoveItem}
                    />
                  ) : (
                    blocks.empty_state && (
                      <MinicartEmptyState
                        settings={blocks.empty_state.settings}
                        block={blocks.empty_state.additional}
                        blocks={blocks}
                        onClose={handleCartClose}
                        showObviyoRecommendations={!!blocks.obviyo_recommendations}
                      />
                    )
                  )}
                </div>
                {blocks.obviyo_recommendations && cartData?.items && cartData.items.length > 0 && (
                  <div
                    className={`product-obviyo-recommendations`}
                    style={{
                      paddingTop: `${blocks.obviyo_recommendations.settings?.padding_top || 0}px`,
                      paddingBottom: `${blocks.obviyo_recommendations.settings?.padding_bottom || 0}px`,
                      paddingLeft: `${blocks.obviyo_recommendations.settings?.padding_left_right || 0}px`,
                      paddingRight: `${blocks.obviyo_recommendations.settings?.padding_left_right || 0}px`,
                      marginTop: `${blocks.obviyo_recommendations.settings?.margin_top || 0}px`,
                      marginBottom: `${blocks.obviyo_recommendations.settings?.margin_bottom || 0}px`,
                      marginLeft: `${blocks.obviyo_recommendations.settings?.margin_left_right || 0}px`,
                      marginRight: `${blocks.obviyo_recommendations.settings?.margin_left_right || 0}px`
                    }}
                  >
                    {hasRecommendations && (
                      <div
                        className={`mb-1 py-2.5 px-4 flex items-center justify-between rounded-t-2xl color-${blocks.obviyo_recommendations.additional?.color_schema}`}
                      >
                        <span
                          className={getCurrentTextStyle(
                            blocks.obviyo_recommendations.settings?.title_style || 'b9--bold',
                            blocks.obviyo_recommendations.settings?.title_style_enable_responsive,
                            blocks.obviyo_recommendations.settings?.title_style_mobile,
                            blocks.obviyo_recommendations.settings?.title_style_tablet,
                            blocks.obviyo_recommendations.settings?.title_style_ultrawide
                          )}
                        >
                          {blocks.obviyo_recommendations.settings?.title || 'You may also like'}
                        </span>
                        <CarouselArrows splideRef={splideRef} />
                      </div>
                    )}
                    <ObviyoProductRecommendations
                      settings={blocks.obviyo_recommendations.settings}
                      ref={splideRef}
                      onRecommendationsLoaded={(loaded) => setHasRecommendations(loaded)}
                      type='cart'
                    />
                  </div>
                )}

                {/* Loading Overlay */}
                {isVisible && isGlobalCartLoading && (
                  <div className='absolute inset-0'>
                    <div
                      className='sticky top-0 bg-white/80 flex items-center justify-center z-[100] w-full rounded-lg pointer-events-none'
                      style={{
                        height: scrollableHeight ? `${scrollableHeight}px` : '100%'
                      }}
                    >
                      <div className='flex items-center gap-3 pointer-events-auto'>
                        <span className='b8--medium text-gray-600 animate-pulse'>Loading...</span>
                        <span className='spinner spinner--md'></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {cartData?.items && cartData.items.length > 0 && cartData && (
              <div className='minicart__bottom border-t border-[#EDEDED]'>
                {blocks.totals && (
                  <MinicartTotals
                    cart_subtotal={cartData.items_subtotal_price ? cartData.items_subtotal_price / 100 : 0}
                    cart_items={cartData.items || []}
                    cart={cartData}
                    settings={blocks.totals.settings}
                    block={blocks.totals.additional}
                  />
                )}

                {blocks.note && blocks.note.settings?.show_cart_note && (
                  <MinicartNote
                    settings={blocks.note.settings}
                    block={blocks.note.additional}
                  />
                )}

                {blocks.actions && (
                  <MinicartActions
                    show_cart={blocks.actions.settings?.show_view_cart}
                    settings={blocks.actions.settings}
                    block={blocks.actions.additional}
                    isLoading={isGlobalCartLoading}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </CSSTransition>
    </>,
    document.querySelector('.header-block-builder') || document.body
  );
};
